import React, { createContext, useContext, useMemo, useReducer } from 'react';
import type { AnyNode, BuilderState, ColumnNode, DocumentModel, ID, RowNode, Breakpoint, ComponentNode } from './types';
import type { BuilderAction } from './actions';
import { addRow as addRowAction, addRowWithLayout as addRowWithLayoutAction, insertRowWithLayoutAt as insertRowWithLayoutAtAction, insertRowWithComponentAt as insertRowWithComponentAtAction, moveRowToIndex as moveRowToIndexAction, setRowLayout as setRowLayoutAction, duplicateNode as duplicateNodeAction, addColumn as addColumnAction, deleteNode as deleteNodeAction, selectNode as selectNodeAction, setBreakpoint as setBreakpointAction, undo as undoAction, redo as redoAction, addComponent as addComponentAction, moveComponentWithinColumn as moveWithinAction, moveComponentBetweenColumns as moveBetweenAction, updateComponentProps as updateComponentPropsAction, setCheckoutLayout as setCheckoutLayoutAction, markSaved as markSavedAction, replaceDocument as replaceDocumentAction } from './actions';

function genId(prefix: string): ID {
  return `${prefix}_${Math.random().toString(36).slice(2, 10)}`;
}

function cloneDoc(doc: DocumentModel): DocumentModel {
  return JSON.parse(JSON.stringify(doc));
}

function getCounts(doc: DocumentModel) {
  const z = (doc.meta && (doc.meta as any).zoneCounts) || { top: 0, side: 0, bottom: 0 };
  return { top: z.top || 0, side: z.side || 0, bottom: z.bottom || 0 } as { top: number; side: number; bottom: number };
}

function setCounts(doc: DocumentModel, counts: { top: number; side: number; bottom: number }) {
  (doc.meta as any) = { ...(doc.meta || {}), zoneCounts: { ...counts } };
}


function zoneForIndex(counts: { top: number; side: number; bottom: number }, index: number): 'top' | 'side' | 'bottom' {
  if (index < counts.top) return 'top';
  if (index < counts.top + counts.side) return 'side';
  return 'bottom';
}

function initialDocument(): DocumentModel {
  const docId = genId('doc');
  // Inicializar com apenas uma linha no topo
  const rowTop: RowNode = {
    id: genId('row'),
    parentId: null,
    type: 'row',
    props: { gap: 16, marginY: 16 },
    children: [],
  };
  const colTop: ColumnNode = {
    id: genId('col'),
    parentId: rowTop.id,
    type: 'column',
    props: { widthByBp: { desktop: 12, tablet: 12, mobile: 12 } },
    children: [],
  };
  rowTop.children = [colTop.id];

  return {
    id: docId,
    name: 'Novo Checkout',
    version: 1,
    meta: {
      zoneCounts: { top: 1, side: 0, bottom: 0 },
      checkoutLayout: 'two-columns' // Configuração da linha do checkout (one-column | two-columns)
    },
    root: [rowTop.id], // Apenas uma linha no topo
    nodes: {
      [rowTop.id]: rowTop,
      [colTop.id]: colTop,
    },
  };

}

const initialState: BuilderState = {
  document: initialDocument(),
  selectionId: null,
  past: [],
  future: [],
  ui: { breakpoint: 'desktop' },
  hasUnsavedChanges: false,
};

function removeSubtree(document: DocumentModel, id: ID) {
  const node = document.nodes[id];
  if (!node) return;
  if (node.type === 'row' || node.type === 'column') {
    const children = (node as RowNode | ColumnNode).children;
    children.forEach((childId) => removeSubtree(document, childId));
  }
  delete document.nodes[id];
}

function builderReducer(state: BuilderState, action: BuilderAction): BuilderState {

  switch (action.type) {
    case 'ADD_ROW': {
      const nextDoc = cloneDoc(state.document);
      const rowId = genId('row');
      const newRow: RowNode = {
        id: rowId,
        parentId: null,
        type: 'row',
        props: { gap: 16, marginY: 16 },
        children: [],
      };
      nextDoc.nodes[rowId] = newRow;
      const counts = getCounts(nextDoc);
      // default add to bottom zone (at the end)
      nextDoc.root = [...nextDoc.root, rowId];
      setCounts(nextDoc, { ...counts, bottom: counts.bottom + 1 });
      return {
        ...state,
        past: [...state.past.slice(-49), state.document],
        future: [],
        document: nextDoc,
        selectionId: rowId,
        hasUnsavedChanges: true,
      };
    }
    case 'DUPLICATE_NODE': {
      const { id } = action;
      const node = state.document.nodes[id] as AnyNode | undefined;
      if (!node) return state;
      const nextDoc = cloneDoc(state.document);
      const cloneCmp = (cmpId: ID, newParent: ID): ID => {
        const src = nextDoc.nodes[cmpId] as ComponentNode;
        const nid = genId('cmp');
        nextDoc.nodes[nid] = { ...src, id: nid, parentId: newParent, props: { ...src.props } };
        return nid;
      };
      const cloneCol = (colId: ID, newParent: ID): ID => {
        const src = nextDoc.nodes[colId] as ColumnNode;
        const nid = genId('col');
        const col: ColumnNode = { id: nid, parentId: newParent, type: 'column', props: { widthByBp: { ...src.props.widthByBp }, align: src.props.align }, children: [] };
        nextDoc.nodes[nid] = col;
        col.children = src.children.map((cid) => cloneCmp(cid, nid));
        return nid;
      };
      const cloneRow = (rowId: ID): ID => {
        const src = nextDoc.nodes[rowId] as RowNode;
        const nid = genId('row');
        const row: RowNode = { id: nid, parentId: null, type: 'row', props: { ...src.props }, children: [] };
        nextDoc.nodes[nid] = row;
        row.children = src.children.map((cid) => cloneCol(cid, nid));
        return nid;
      };
      if (node.type === 'component') {
        const col = nextDoc.nodes[node.parentId as ID] as ColumnNode | undefined;
        if (!col) return state;
        const idx = col.children.indexOf(id);
        const nid = cloneCmp(id, col.id);
        col.children.splice(idx + 1, 0, nid);
        return { ...state, past: [...state.past.slice(-49), state.document], future: [], document: nextDoc, selectionId: nid };
      }
      if (node.type === 'column') {
        const row = nextDoc.nodes[node.parentId as ID] as RowNode | undefined;
        if (!row) return state;
        const idx = row.children.indexOf(id);
        const nid = cloneCol(id, row.id);
        row.children.splice(idx + 1, 0, nid);
        return { ...state, past: [...state.past.slice(-49), state.document], future: [], document: nextDoc, selectionId: nid };
      }
      if (node.type === 'row') {
        const idx = nextDoc.root.indexOf(id);
        const nid = cloneRow(id);
        nextDoc.root.splice(idx + 1, 0, nid);
        return { ...state, past: [...state.past.slice(-49), state.document], future: [], document: nextDoc, selectionId: nid };
      }
      return state;
    }
    case 'ADD_ROW_WITH_LAYOUT': {
      const { layout } = action;
      const nextDoc = cloneDoc(state.document);
      const rowId = genId('row');
      const newRow: RowNode = {
        id: rowId,
        parentId: null,
        type: 'row',
        props: { gap: 16, marginY: 16 },
        children: [],
      };
      nextDoc.nodes[rowId] = newRow;
      // criar colunas conforme layout (spans somando 12)
      layout.forEach((span) => {
        const colId = genId('col');
        const col: ColumnNode = {
          id: colId,
          parentId: rowId,
          type: 'column',
          props: { widthByBp: { desktop: span, tablet: span, mobile: 12 }, align: 'start' },
          children: [],
        };
        nextDoc.nodes[colId] = col;
        newRow.children.push(colId);
      });
      nextDoc.root = [...nextDoc.root, rowId];
      return {
        ...state,
        past: [...state.past.slice(-49), state.document],
        future: [],
        document: nextDoc,
        selectionId: rowId,
      };
    }
    case 'INSERT_ROW_WITH_LAYOUT_AT': {
      const { layout, index } = action;
      const nextDoc = cloneDoc(state.document);
      const rowId = genId('row');
      const newRow: RowNode = {
        id: rowId,
        parentId: null,
        type: 'row',
        props: { gap: 16, marginY: 16 },
        children: [],
      };
      nextDoc.nodes[rowId] = newRow;
      layout.forEach((span) => {
        const colId = genId('col');
        const col: ColumnNode = {
          id: colId,
          parentId: rowId,
          type: 'column',
          props: { widthByBp: { desktop: span, tablet: span, mobile: 12 }, align: 'start' },
          children: [],
        };
        nextDoc.nodes[colId] = col;
        newRow.children.push(colId);
      });
      const counts = getCounts(nextDoc);
      const insertAt = Math.max(0, Math.min(index, nextDoc.root.length));
      const newRoot = Array.from(nextDoc.root);
      newRoot.splice(insertAt, 0, rowId);
      nextDoc.root = newRoot;
      const z = zoneForIndex(counts, insertAt);
      const newCounts = { ...counts, [z]: counts[z] + 1 } as any;
      setCounts(nextDoc, newCounts);
      return {
        ...state,
        past: [...state.past.slice(-49), state.document],
        future: [],
        document: nextDoc,
        selectionId: rowId,
      };
    }
    case 'INSERT_ROW_WITH_COMPONENT_AT': {
      const { layout, index, compType, initialProps } = action;
      const nextDoc = cloneDoc(state.document);
      const effectiveLayout = (layout && layout.length > 0) ? layout : [12];
      const rowId = genId('row');
      const newRow: RowNode = {
        id: rowId,
        parentId: null,
        type: 'row',
        props: { gap: 16, marginY: 16 },
        children: [],
      };
      nextDoc.nodes[rowId] = newRow;
      effectiveLayout.forEach((span) => {
        const colId = genId('col');
        const col: ColumnNode = {
          id: colId,
          parentId: rowId,
          type: 'column',
          props: { widthByBp: { desktop: span, tablet: span, mobile: 12 }, align: 'start' },
          children: [],
        };
        nextDoc.nodes[colId] = col;
        newRow.children.push(colId);
      });
      // inserir componente na primeira coluna
      const firstColId = newRow.children[0];
      if (firstColId) {
        const cmpId = genId('cmp');
        nextDoc.nodes[cmpId] = {
          id: cmpId,
          parentId: firstColId,
          type: 'component',
          compType,
          props: { ...(initialProps || {}) },
        } as ComponentNode;
        const firstCol = nextDoc.nodes[firstColId] as ColumnNode;
        firstCol.children = [cmpId];
      }
      const counts = getCounts(nextDoc);
      const insertAt = Math.max(0, Math.min(index, nextDoc.root.length));
      const newRoot = Array.from(nextDoc.root);
      newRoot.splice(insertAt, 0, rowId);
      nextDoc.root = newRoot;
      const z = zoneForIndex(counts, insertAt);
      const newCounts = { ...counts, [z]: counts[z] + 1 } as any;
      setCounts(nextDoc, newCounts);
      return {
        ...state,
        past: [...state.past.slice(-49), state.document],
        future: [],
        document: nextDoc,
        selectionId: (nextDoc.nodes[(nextDoc.nodes[newRow.children[0]] as ColumnNode)?.children[0]]?.id) || rowId,
      };
    }
    case 'ADD_COLUMN': {
      const { rowId } = action;
      const row = state.document.nodes[rowId] as RowNode | undefined;
      if (!row || row.type !== 'row') return state;
      const nextDoc = cloneDoc(state.document);
      const nextRow = nextDoc.nodes[rowId] as RowNode;
      const colId = genId('col');
      const newCol: ColumnNode = {
        id: colId,
        parentId: rowId,
        type: 'column',
        props: {
          widthByBp: { desktop: 12, tablet: 12, mobile: 12 },
          align: 'start',
        },
        children: [],
      };
      nextDoc.nodes[colId] = newCol;
      nextRow.children = [...nextRow.children, colId];
      return {
        ...state,
        past: [...state.past.slice(-49), state.document],
        future: [],
        document: nextDoc,
        selectionId: colId,
      };
    }
    case 'SET_ROW_LAYOUT': {
      const { rowId, layout } = action;
      const row = state.document.nodes[rowId] as RowNode | undefined;
      if (!row || row.type !== 'row') return state;
      const nextDoc = cloneDoc(state.document);
      const nextRow = nextDoc.nodes[rowId] as RowNode;
      const existingCols = nextRow.children.map((cid) => nextDoc.nodes[cid] as ColumnNode);
      const newChildren: ID[] = [];
      const makeCol = (span: number): ColumnNode => ({
        id: genId('col'),
        parentId: rowId,
        type: 'column',
        props: { widthByBp: { desktop: span, tablet: span, mobile: 12 }, align: 'start' },
        children: [],
      });
      layout.forEach((span, idx) => {
        const reuse = existingCols[idx];
        if (reuse) {
          reuse.props.widthByBp = { desktop: span, tablet: span, mobile: 12 };
          newChildren.push(reuse.id);
        } else {
          const col = makeCol(span);
          nextDoc.nodes[col.id] = col;
          newChildren.push(col.id);
        }
      });
      // mover conteúdos das colunas que sobraram para a primeira nova coluna
      if (existingCols.length > layout.length && newChildren.length > 0) {
        const target = nextDoc.nodes[newChildren[0]] as ColumnNode;
        for (let i = layout.length; i < existingCols.length; i++) {
          const extra = existingCols[i];
          extra.children.forEach((cid) => {
            target.children.push(cid);
            const cmp = nextDoc.nodes[cid] as ComponentNode | undefined;
            if (cmp && cmp.type === 'component') cmp.parentId = target.id;
          });
          removeSubtree(nextDoc, extra.id);
        }
      }
      nextRow.children = newChildren;
      return {
        ...state,
        past: [...state.past.slice(-49), state.document],
        future: [],
        document: nextDoc,
      };
    }
    case 'MOVE_ROW_TO_INDEX': {
      const { rowId, index } = action;
      if (!state.document.root.includes(rowId)) return state;
      const nextDoc = cloneDoc(state.document);
      const fromIdx = nextDoc.root.indexOf(rowId);
      if (fromIdx === -1) return state;
      const counts = getCounts(nextDoc);
      const newRoot = Array.from(nextDoc.root);
      newRoot.splice(fromIdx, 1);
      const toIdx = Math.max(0, Math.min(index, newRoot.length));
      newRoot.splice(toIdx, 0, rowId);
      nextDoc.root = newRoot;
      // adjust counts if zone changed
      const fromZone = zoneForIndex(counts, fromIdx);
      const countsAfterRemoval = { ...counts, [fromZone]: counts[fromZone] - 1 } as any;
      const toZone = zoneForIndex(countsAfterRemoval, toIdx);
      const finalCounts = { ...countsAfterRemoval, [toZone]: countsAfterRemoval[toZone] + 1 } as any;
      setCounts(nextDoc, finalCounts);
      return {
        ...state,
        past: [...state.past.slice(-49), state.document],
        future: [],
        document: nextDoc,
        selectionId: rowId,
      };
    }
    case 'DELETE_NODE': {
      const { id } = action;
      if (!state.document.nodes[id] && !state.document.root.includes(id)) return state;
      const nextDoc = cloneDoc(state.document);
      const node = nextDoc.nodes[id] as AnyNode | undefined;
      if (node) {
        // Remove from parent
        const parentId = node.parentId;
        if (parentId) {
          const parent = nextDoc.nodes[parentId] as RowNode | ColumnNode | undefined;
          if (parent && (parent.type === 'row' || parent.type === 'column')) {
            parent.children = parent.children.filter((cid) => cid !== id);
          }
        } else {
          // root level (row)
          const idx = nextDoc.root.indexOf(id);
          const counts = getCounts(nextDoc);
          const z = idx >= 0 ? zoneForIndex(counts, idx) : null;
          nextDoc.root = nextDoc.root.filter((rid) => rid !== id);
          if (z) {
            const newCounts = { ...counts, [z]: counts[z] - 1 } as any;
            setCounts(nextDoc, newCounts);
          }
        }
        removeSubtree(nextDoc, id);
      } else {
        // Might be a root row id without node (shouldn't happen), still filter root
        nextDoc.root = nextDoc.root.filter((rid) => rid !== id);
      }
      return {
        ...state,
        past: [...state.past.slice(-49), state.document],
        future: [],
        document: nextDoc,
        selectionId: state.selectionId === id ? null : state.selectionId,
      };
    }
    case 'SELECT_NODE': {
      return { ...state, selectionId: action.id };
    }
    case 'SET_BREAKPOINT': {
      return { ...state, ui: { ...state.ui, breakpoint: action.breakpoint } };
    }
    case 'UNDO': {
      if (state.past.length === 0) return state;
      const previous = state.past[state.past.length - 1];
      const newPast = state.past.slice(0, -1);
      return {
        ...state,
        document: previous,
        past: newPast,
        future: [state.document, ...state.future].slice(0, 50),
      };
    }
    case 'REDO': {
      if (state.future.length === 0) return state;
      const [next, ...rest] = state.future;
      return {
        ...state,
        document: next,
        past: [...state.past.slice(-49), state.document],
        future: rest,
      };
    }
    case 'REPLACE_DOCUMENT': {
      const newDoc = cloneDoc(action.document);
      // Garantir que meta existe e tem valores padrão
      if (!newDoc.meta) {
        newDoc.meta = {};
      }
      if (!(newDoc.meta as any).checkoutLayout) {
        (newDoc.meta as any).checkoutLayout = 'two-columns';
      }
      if (!(newDoc.meta as any).zoneCounts) {
        (newDoc.meta as any).zoneCounts = { top: 0, side: 0, bottom: 0 };
      }
      return {
        ...state,
        past: [], // Limpar histórico ao carregar documento
        future: [],
        document: newDoc,
        hasUnsavedChanges: false, // Marcar como salvo ao carregar
      };
    }
    case 'ADD_COMPONENT': {
      const { columnId, compType, index, initialProps } = action;
      const col = state.document.nodes[columnId] as ColumnNode | undefined;
      if (!col || col.type !== 'column') return state;
      const nextDoc = cloneDoc(state.document);
      const nextCol = nextDoc.nodes[columnId] as ColumnNode;
      const cmpId = genId('cmp');
      const newCmp: ComponentNode = {
        id: cmpId,
        parentId: columnId,
        type: 'component',
        compType,
        props: { ...(initialProps || {}) },
      };
      nextDoc.nodes[cmpId] = newCmp;
      const insertIndex = typeof index === 'number' ? Math.max(0, Math.min(index, nextCol.children.length)) : nextCol.children.length;
      nextCol.children = [
        ...nextCol.children.slice(0, insertIndex),
        cmpId,
        ...nextCol.children.slice(insertIndex),
      ];
      return {
        ...state,
        past: [...state.past.slice(-49), state.document],
        future: [],
        document: nextDoc,
        selectionId: cmpId,
      };
    }
    case 'MOVE_COMPONENT_WITHIN_COLUMN': {
      const { columnId, from, to } = action;
      const col = state.document.nodes[columnId] as ColumnNode | undefined;
      if (!col || col.type !== 'column') return state;
      if (from === to) return state;
      const nextDoc = cloneDoc(state.document);
      const nextCol = nextDoc.nodes[columnId] as ColumnNode;
      const arr = Array.from(nextCol.children);
      const [removed] = arr.splice(from, 1);
      arr.splice(to, 0, removed);
      nextCol.children = arr;
      return {
        ...state,
        past: [...state.past.slice(-49), state.document],
        future: [],
        document: nextDoc,
      };
    }
    case 'UPDATE_COMPONENT_PROPS': {
      const { id, props } = action;
      const cmp = state.document.nodes[id] as ComponentNode | undefined;
      if (!cmp || cmp.type !== 'component') return state;
      const nextDoc = cloneDoc(state.document);
      const nextCmp = nextDoc.nodes[id] as ComponentNode;
      nextCmp.props = { ...nextCmp.props, ...props };
      return {
        ...state,
        past: [...state.past.slice(-49), state.document],
        future: [],
        document: nextDoc,
      };
    }
    case 'MOVE_COMPONENT_BETWEEN_COLUMNS': {
      const { fromColumnId, toColumnId, from, to } = action;
      const fromCol = state.document.nodes[fromColumnId] as ColumnNode | undefined;
      const toCol = state.document.nodes[toColumnId] as ColumnNode | undefined;
      if (!fromCol || !toCol || fromCol.type !== 'column' || toCol.type !== 'column') return state;
      const nextDoc = cloneDoc(state.document);
      const nFrom = nextDoc.nodes[fromColumnId] as ColumnNode;
      const nTo = nextDoc.nodes[toColumnId] as ColumnNode;
      const fromArr = Array.from(nFrom.children);
      const [movedId] = fromArr.splice(from, 1);
      const toArr = Array.from(nTo.children);
      toArr.splice(to, 0, movedId);
      nFrom.children = fromArr;
      nTo.children = toArr;
      const movedNode = nextDoc.nodes[movedId] as ComponentNode | undefined;
      if (movedNode && movedNode.type === 'component') movedNode.parentId = toColumnId;
      return {
        ...state,
        past: [...state.past.slice(-49), state.document],
        future: [],
        document: nextDoc,
      };
    }
    case 'SET_CHECKOUT_LAYOUT': {
      const { layout } = action;
      const nextDoc = cloneDoc(state.document);
      // Garantir que meta existe antes de definir checkoutLayout
      if (!nextDoc.meta) {
        nextDoc.meta = {};
      }
      (nextDoc.meta as any).checkoutLayout = layout;
      return {
        ...state,
        past: [...state.past.slice(-49), state.document],
        future: [],
        document: nextDoc,
      };
    }
    case 'MARK_SAVED': {
      return { ...state, hasUnsavedChanges: false };
    }
    default:
      return state;
  }
}

type BuilderContextType = {
  state: BuilderState;
  addRow: () => void;
  addRowWithLayout: (layout: number[]) => void;
  insertRowWithLayoutAt: (layout: number[], index: number) => void;
  insertRowWithComponentAt: (layout: number[], index: number, compType: string, initialProps?: Record<string, any>) => void;
  moveRowToIndex: (rowId: ID, index: number) => void;
  addColumn: (rowId: ID) => void;
  deleteNode: (id: ID) => void;
  duplicateNode: (id: ID) => void;
  selectNode: (id: ID | null) => void;
  setBreakpoint: (bp: Breakpoint) => void;
  setRowLayout: (rowId: ID, layout: number[]) => void;
  undo: () => void;
  redo: () => void;
  addComponent: (columnId: ID, compType: string, index?: number, initialProps?: Record<string, any>) => void;
  moveComponentWithinColumn: (columnId: ID, from: number, to: number) => void;
  moveComponentBetweenColumns: (fromColumnId: ID, toColumnId: ID, from: number, to: number) => void;
  updateComponentProps: (id: ID, props: Record<string, any>) => void;
  setCheckoutLayout: (layout: 'one-column' | 'two-columns') => void;
  markSaved: () => void;
  replaceDocument: (document: DocumentModel) => void;
  // Novas funcionalidades
  saveDocument: (name?: string) => Promise<{ success: boolean; id?: string; error?: string }>;
  loadDocument: (id: string) => Promise<{ success: boolean; error?: string }>;
  exportDocument: () => string;
  importDocument: (data: string) => { success: boolean; error?: string };
};

const Ctx = createContext<BuilderContextType | null>(null);

// Wrapper reducer que automaticamente marca mudanças não salvas
function builderReducerWithChangeTracking(state: BuilderState, action: BuilderAction): BuilderState {
  const newState = builderReducer(state, action);

  // Se o estado mudou e não é uma ação que não deve marcar como não salvo
  if (newState !== state &&
    action.type !== 'SELECT_NODE' &&
    action.type !== 'SET_BREAKPOINT' &&
    action.type !== 'MARK_SAVED' &&
    action.type !== 'REPLACE_DOCUMENT') { // Não marcar como não salvo ao carregar documento
    return { ...newState, hasUnsavedChanges: true };
  }

  return newState;
}

export const BuilderProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [state, dispatch] = useReducer(builderReducerWithChangeTracking, initialState);

  const api = useMemo<BuilderContextType>(() => ({
    state,
    addRow: () => dispatch(addRowAction()),
    addRowWithLayout: (layout: number[]) => dispatch(addRowWithLayoutAction(layout)),
    insertRowWithLayoutAt: (layout: number[], index: number) => dispatch(insertRowWithLayoutAtAction(layout, index)),
    insertRowWithComponentAt: (layout: number[], index: number, compType: string, initialProps?: Record<string, any>) => dispatch(insertRowWithComponentAtAction(layout, index, compType, initialProps)),
    moveRowToIndex: (rowId: ID, index: number) => dispatch(moveRowToIndexAction(rowId, index)),
    addColumn: (rowId: ID) => dispatch(addColumnAction(rowId)),
    deleteNode: (id: ID) => dispatch(deleteNodeAction(id)),
    duplicateNode: (id: ID) => dispatch(duplicateNodeAction(id)),
    selectNode: (id: ID | null) => dispatch(selectNodeAction(id)),
    setBreakpoint: (bp: Breakpoint) => dispatch(setBreakpointAction(bp)),
    setRowLayout: (rowId: ID, layout: number[]) => dispatch(setRowLayoutAction(rowId, layout)),
    undo: () => dispatch(undoAction()),
    redo: () => dispatch(redoAction()),
    addComponent: (columnId: ID, compType: string, index?: number, initialProps?: Record<string, any>) => dispatch(addComponentAction(columnId, compType, index, initialProps)),
    moveComponentWithinColumn: (columnId: ID, from: number, to: number) => dispatch(moveWithinAction(columnId, from, to)),
    moveComponentBetweenColumns: (fromColumnId: ID, toColumnId: ID, from: number, to: number) => dispatch(moveBetweenAction(fromColumnId, toColumnId, from, to)),
    updateComponentProps: (id: ID, props: Record<string, any>) => dispatch(updateComponentPropsAction(id, props)),
    setCheckoutLayout: (layout: 'one-column' | 'two-columns') => dispatch(setCheckoutLayoutAction(layout)),
    markSaved: () => dispatch(markSavedAction()),
    replaceDocument: (document: DocumentModel) => dispatch(replaceDocumentAction(document)),
    // Implementação das novas funcionalidades
    saveDocument: async (name?: string) => {
      try {
        // Implementar salvamento no backend aqui
        console.log('Salvando documento:', { name, document: state.document });

        // Simular chamada API
        await new Promise(resolve => setTimeout(resolve, 500));

        dispatch(markSavedAction());
        return { success: true, id: 'mock-id' };
      } catch (error) {
        console.error('Erro ao salvar documento:', error);
        return { success: false, error: 'Erro interno do servidor' };
      }
    },
    loadDocument: async (id: string) => {
      try {
        // Implementar carregamento do backend aqui
        console.log('Carregando documento:', id);

        // Simular chamada API
        await new Promise(resolve => setTimeout(resolve, 500));

        return { success: true };
      } catch (error) {
        console.error('Erro ao carregar documento:', error);
        return { success: false, error: 'Documento não encontrado' };
      }
    },
    exportDocument: () => {
      return JSON.stringify(state.document, null, 2);
    },
    importDocument: (data: string) => {
      try {
        const parsed = JSON.parse(data);
        // Validar estrutura do documento aqui
        dispatch({ type: 'REPLACE_DOCUMENT', document: parsed });
        return { success: true };
      } catch (error) {
        console.error('Erro ao importar documento:', error);
        return { success: false, error: 'Formato de documento inválido' };
      }
    },
  }), [state, dispatch]);

  return <Ctx.Provider value={api}>{children}</Ctx.Provider>;
};

export function useBuilderStore() {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error('useBuilderStore deve ser usado dentro de BuilderProvider');
  return ctx;
}
